/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { EndpointBasicAuthRepresentation } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
export class AdminEndpointsApi extends BaseApi {
    createBasicAuthConfiguration(createRepresentation) {
        throwIfNotDefined(createRepresentation, 'createRepresentation');
        return this.post({
            path: '/api/enterprise/admin/basic-auths',
            bodyParam: createRepresentation,
            returnType: EndpointBasicAuthRepresentation
        });
    }
    createEndpointConfiguration(representation) {
        throwIfNotDefined(representation, 'representation');
        return this.post({
            path: '/api/enterprise/admin/endpoints',
            bodyParam: representation
        });
    }
    getBasicAuthConfiguration(basicAuthId, tenantId) {
        throwIfNotDefined(basicAuthId, 'basicAuthId');
        throwIfNotDefined(tenantId, 'tenantId');
        const pathParams = {
            basicAuthId
        };
        const queryParams = {
            tenantId
        };
        return this.get({
            path: '/api/enterprise/admin/basic-auths/{basicAuthId}',
            pathParams,
            queryParams,
            returnType: EndpointBasicAuthRepresentation
        });
    }
    getBasicAuthConfigurations(tenantId) {
        throwIfNotDefined(tenantId, 'tenantId');
        const queryParams = {
            tenantId
        };
        return this.get({
            path: '/api/enterprise/admin/basic-auths',
            queryParams,
            returnType: EndpointBasicAuthRepresentation
        });
    }
    getEndpointConfiguration(endpointConfigurationId, tenantId) {
        throwIfNotDefined(endpointConfigurationId, 'endpointConfigurationId');
        throwIfNotDefined(tenantId, 'tenantId');
        const pathParams = {
            endpointConfigurationId
        };
        const queryParams = {
            tenantId
        };
        return this.get({
            path: '/api/enterprise/admin/endpoints/{endpointConfigurationId}',
            pathParams,
            queryParams
        });
    }
    getEndpointConfigurations(tenantId) {
        throwIfNotDefined(tenantId, 'tenantId');
        const queryParams = {
            tenantId
        };
        return this.get({
            path: '/api/enterprise/admin/endpoints',
            queryParams
        });
    }
    removeBasicAuthConfiguration(basicAuthId, tenantId) {
        throwIfNotDefined(basicAuthId, 'basicAuthId');
        throwIfNotDefined(tenantId, 'tenantId');
        const pathParams = {
            basicAuthId
        };
        const queryParams = {
            tenantId
        };
        return this.delete({
            path: '/api/enterprise/admin/basic-auths/{basicAuthId}',
            pathParams,
            queryParams
        });
    }
    removeEndpointConfiguration(endpointConfigurationId, tenantId) {
        throwIfNotDefined(endpointConfigurationId, 'endpointConfigurationId');
        throwIfNotDefined(tenantId, 'tenantId');
        const pathParams = {
            endpointConfigurationId
        };
        const queryParams = {
            tenantId
        };
        return this.delete({
            path: '/api/enterprise/admin/endpoints/{endpointConfigurationId}',
            pathParams,
            queryParams
        });
    }
    updateBasicAuthConfiguration(basicAuthId, createRepresentation) {
        throwIfNotDefined(basicAuthId, 'basicAuthId');
        throwIfNotDefined(createRepresentation, 'createRepresentation');
        const pathParams = {
            basicAuthId
        };
        return this.put({
            path: '/api/enterprise/admin/basic-auths/{basicAuthId}',
            pathParams,
            bodyParam: createRepresentation,
            returnType: EndpointBasicAuthRepresentation
        });
    }
    updateEndpointConfiguration(endpointConfigurationId, representation) {
        throwIfNotDefined(endpointConfigurationId, 'endpointConfigurationId');
        throwIfNotDefined(representation, 'representation');
        const pathParams = {
            endpointConfigurationId
        };
        return this.put({
            path: '/api/enterprise/admin/endpoints/{endpointConfigurationId}',
            pathParams,
            bodyParam: representation
        });
    }
}
